import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';
import { track } from '@/utils/tracker';

export default function WatermarkRemovePage() {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    user,
    pointsCost,
    controls,
  } = useToolGenerate('watermark_remove');

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result as string);
      track.referenceUploadSuccess({ source: 'tool_watermark_remove', count: 1 });
    };
    reader.readAsDataURL(file);
  }, []);

  const canGenerate = !!image;

  const handleGenerate = () => {
    if (!canGenerate) return;
    doGenerate('', image ?? undefined);
  };

  return (
    <ToolPageLayout
      title={t('extool_watermarkRemove')}
      icon="ri-contrast-drop-fill"
      iconBg="linear-gradient(135deg, #6366F1, #818CF8)"
      iconColor="#6366F1"
      description={t('extool_watermarkRemove_desc')}
      guidance={{
        input: t('watermark_remove_inputRequirement'),
        output: t('watermark_remove_expectedResult'),
        fallback: t('watermark_remove_fallbackTip'),
        beforeHint: t('watermark_remove_beforeHint'),
        afterHint: t('watermark_remove_afterHint'),
      }}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={canGenerate}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      {/* Upload image */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #6366F1, #818CF8)' }}
          >
            <i className="ri-image-add-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('watermark_remove_uploadLabel')}</span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {image ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-3"
            style={{ aspectRatio: '16/9', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={image} alt="upload" className="w-full h-full object-contain" />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => pickPhotoFromLibrary(fileInputRef, setImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '16/9',
              background: 'rgba(99,102,241,0.08)',
              border: '2px dashed rgba(99,102,241,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(99,102,241,0.12)' }}
            >
              <i className="ri-upload-cloud-2-line text-2xl text-indigo-400" />
            </div>
            <p className="text-sm text-gray-500">{t('watermark_remove_uploadPlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
